import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, ImagePlus, Trash2, Loader2, X as XIcon, ImageIcon, Star, ChevronLeft, ChevronRight } from "lucide-react";

const MAX_PHOTOS = 12;
const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
const abs = (u) => (u && u.startsWith("/") ? `${backendUrl}${u}` : u);

/**
 * PhotoGallery — self-managed gallery grid used in /profile.
 * Handles upload (camera OR file), delete, promote-to-avatar, caption edit,
 * and a lightbox viewer. The ID photo (id_photo_url) is intentionally NOT
 * shown here — it stays passport-style and system-locked.
 */
export default function PhotoGallery({ onAvatarChanged }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/profile/photos");
      setPhotos(r.data.photos || []);
    } catch { /* empty is OK */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`You can have up to ${MAX_PHOTOS} gallery photos. Remove one first.`);
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("kind", "profile_photo");
      const up = await api.post("/files/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.post("/profile/photos", { file_id: up.data.id });
      setPhotos((prev) => [...prev, r.data.photo]);
      toast.success("Photo added to your profile");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (libraryRef.current) libraryRef.current.value = "";
    }
  };

  const removePhoto = async (photoId) => {
    if (!window.confirm("Remove this photo from your profile?")) return;
    setBusyId(photoId);
    try {
      await api.delete(`/profile/photos/${photoId}`);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      toast.success("Photo removed");
      if (lightboxIdx !== null) setLightboxIdx(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setBusyId(null); }
  };

  const makeAvatar = async (photoId) => {
    setBusyId(photoId);
    try {
      const r = await api.post(`/profile/photos/${photoId}/set-avatar`);
      toast.success("Profile picture updated");
      onAvatarChanged && onAvatarChanged(r.data.user);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't set as profile picture");
    } finally { setBusyId(null); }
  };

  return (
    <div className="mt-6" data-testid="photo-gallery">
      <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// PHOTO GALLERY</div>
          <h3 className="font-display text-xl font-semibold text-white tracking-tight mt-0.5">Show pilots who you are</h3>
          <p className="text-white/55 text-xs mt-1">Add ramp shots, cockpit selfies, or your favorite aviation moments. Up to {MAX_PHOTOS} photos. Your official ID photo is kept separate.</p>
        </div>
        <div className="mono text-[10px] tracking-widest uppercase text-white/50">
          {photos.length} / {MAX_PHOTOS}
        </div>
      </div>

      {/* Hidden inputs */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => uploadPhoto(e.target.files?.[0])}
        data-testid="gallery-camera-input"
      />
      <input
        ref={libraryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => uploadPhoto(e.target.files?.[0])}
        data-testid="gallery-library-input"
      />

      {/* Upload actions */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          type="button"
          onClick={() => cameraRef.current?.click()}
          disabled={uploading || photos.length >= MAX_PHOTOS}
          data-testid="gallery-camera-btn"
          className="mono text-[11px] tracking-widest uppercase px-4 py-2.5 rounded bg-cyan text-obsidian font-bold hover:brightness-110 disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
          Take Photo
        </button>
        <button
          type="button"
          onClick={() => libraryRef.current?.click()}
          disabled={uploading || photos.length >= MAX_PHOTOS}
          data-testid="gallery-library-btn"
          className="mono text-[11px] tracking-widest uppercase px-4 py-2.5 rounded bg-white/[0.06] border border-white/15 text-white hover:border-cyan/50 disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          <ImagePlus className="h-3.5 w-3.5" />
          Upload From Library
        </button>
      </div>

      {loading ? (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-8 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-cyan" />
        </div>
      ) : photos.length === 0 ? (
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-8 text-center" data-testid="gallery-empty">
          <ImageIcon className="h-8 w-8 text-white/30 mx-auto mb-3" />
          <div className="text-white/80 text-sm">No photos yet.</div>
          <div className="text-white/50 text-xs mt-1">Add a few photos so other pilots can put a face to the name.</div>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="gallery-grid">
          {photos.map((p, idx) => (
            <div
              key={p.id}
              className="relative aspect-square rounded-lg overflow-hidden border border-white/10 bg-black group"
              data-testid={`gallery-photo-${p.id}`}
            >
              <img
                src={abs(p.url)}
                alt={p.caption || ""}
                className="w-full h-full object-cover cursor-pointer transition-transform group-hover:scale-105"
                onClick={() => setLightboxIdx(idx)}
              />
              {/* Overlay actions */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute top-1.5 right-1.5 flex gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); makeAvatar(p.id); }}
                    disabled={busyId === p.id}
                    data-testid={`gallery-set-avatar-${p.id}`}
                    className="h-7 w-7 rounded-full bg-black/70 hover:bg-cyan hover:text-obsidian text-white grid place-items-center transition-colors"
                    title="Make this my profile picture"
                  >
                    {busyId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Star className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); removePhoto(p.id); }}
                    disabled={busyId === p.id}
                    data-testid={`gallery-remove-${p.id}`}
                    className="h-7 w-7 rounded-full bg-black/70 hover:bg-red-500 text-white grid place-items-center transition-colors"
                    title="Remove photo"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {p.caption && (
                  <div className="absolute bottom-1.5 left-1.5 right-1.5 text-[10px] text-white/90 line-clamp-2">
                    {p.caption}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIdx !== null && photos[lightboxIdx] && (
        <Lightbox
          photos={photos}
          index={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          onIndex={(i) => setLightboxIdx(i)}
          onDelete={(id) => removePhoto(id)}
          onSetAvatar={(id) => makeAvatar(id)}
          busyId={busyId}
        />
      )}
    </div>
  );
}

/**
 * PublicGallery — read-only version shown on /crew/:id for visitors.
 */
export function PublicGallery({ photos }) {
  const [idx, setIdx] = useState(null);
  if (!photos || photos.length === 0) return null;
  return (
    <div className="mt-6" data-testid="public-gallery">
      <div className="mono text-[10px] tracking-[0.24em] uppercase text-white/45 mb-2">// PHOTOS · {photos.length}</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
        {photos.map((p, i) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setIdx(i)}
            className="relative aspect-square rounded-lg overflow-hidden border border-white/10 bg-black hover:border-cyan/40 transition-colors"
            data-testid={`public-gallery-photo-${p.id}`}
          >
            <img src={abs(p.url)} alt={p.caption || ""} className="w-full h-full object-cover" loading="lazy" />
          </button>
        ))}
      </div>
      {idx !== null && photos[idx] && (
        <Lightbox
          photos={photos}
          index={idx}
          onClose={() => setIdx(null)}
          onIndex={(i) => setIdx(i)}
        />
      )}
    </div>
  );
}

/**
 * Simple keyboard-navigable lightbox. Owner variant shows delete + set-avatar.
 */
function Lightbox({ photos, index, onClose, onIndex, onDelete, onSetAvatar, busyId }) {
  const cur = photos[index];
  const prev = () => onIndex((index - 1 + photos.length) % photos.length);
  const next = () => onIndex((index + 1) % photos.length);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);
  if (!cur) return null;
  return (
    <div
      className="fixed inset-0 z-[999] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="gallery-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
        data-testid="lightbox-close"
      >
        <XIcon className="h-5 w-5" />
      </button>
      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
            data-testid="lightbox-next"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}
      <div className="max-w-4xl w-full max-h-full flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
        <img src={abs(cur.url)} alt={cur.caption || ""} className="max-h-[80vh] w-auto object-contain rounded-lg" />
        {cur.caption && <div className="text-white/80 text-sm text-center max-w-lg">{cur.caption}</div>}
        <div className="mono text-[10px] tracking-widest uppercase text-white/50">{index + 1} / {photos.length}</div>
        {(onDelete || onSetAvatar) && (
          <div className="flex gap-2">
            {onSetAvatar && (
              <button
                onClick={() => onSetAvatar(cur.id)}
                disabled={busyId === cur.id}
                data-testid="lightbox-set-avatar"
                className="mono text-[10px] tracking-widest uppercase px-3.5 py-2 rounded bg-cyan text-obsidian font-bold hover:brightness-110 inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                {busyId === cur.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Star className="h-3 w-3" />} Set as profile picture
              </button>
            )}
            {onDelete && (
              <button
                onClick={() => onDelete(cur.id)}
                disabled={busyId === cur.id}
                data-testid="lightbox-delete"
                className="mono text-[10px] tracking-widest uppercase px-3.5 py-2 rounded border border-white/20 text-white/80 hover:border-red-400 hover:text-red-400 inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                <Trash2 className="h-3 w-3" /> Delete
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
